<template>
    <page-loading v-if="loading"></page-loading>
    <page-container v-else>
        <step-form-heading v-if="!props.authConfigId"
            :icon="faFingerprint">
            <experimental-pill class="-mt-10 -mr-4 mb-2"
                type="beta"></experimental-pill>
            <div>
                New API Auth
            </div>

            <template v-slot:description>
                Store API credentials safe and securely for reuse in your automations.
            </template>
        </step-form-heading>
        <page-header v-if="props.authConfigId"
            class="mb-6 lg:mb-12">
            <template v-slot:subtitle>
                <a href="/settings/integrations#/auth-configs"
                    class="no-underline! text-sky-600! text-base font-normal">
                    <font-awesome-icon widthAuto class="mr-1"
                        :icon="faArrowLeft"></font-awesome-icon>
                    All API Auths
                </a>
            </template>

            {{ data.friendly_name }}
        </page-header>
        <div :class="{
            'grid grid-cols-12': props.authConfigId,
        }">
            <page-sidebar v-if="props.authConfigId"
                :links="[
                    {
                        label: 'Details',
                        href: '/settings/integrations/auth-configs/' + props.authConfigId,
                    },
                    {
                        label: data.auth_config_type === 'oauth2_client_credentials' ? 'OAuth' : 'Headers',
                        href: '/settings/integrations/auth-configs/' + props.authConfigId + '/credentials',
                    },
                ]"></page-sidebar>

            <vx-sticky-nav :sections="[
                { id: 'name', label: 'Name' },
                { id: data.auth_config_type === 'oauth2_client_credentials' ? 'oauth' : 'headers', label: data.auth_config_type === 'oauth2_client_credentials' ? 'OAuth' : 'Headers' },
            ]"
                class="col-span-12 lg:col-span-8 lg:col-start-3"
                :navClasses="`${props.authConfigId ? 'hidden' : ''}`">

                <form @submit.prevent="submit()"
                    autocomplete="off"
                    id="auth-config-form"
                    class="flex flex-col mx-auto items-center gap-2 md:gap-8 w-full max-w-(--breakpoint-sm)">
                    <div v-if="data.authConfigId && !props.credentials"
                        class="text-2xl font-extrabold w-full">
                        Details</div>
                    <vx-expandable-card v-if="!props.credentials"
                        id="name">
                        <template v-slot:content>
                            <auth-config-form-name v-model="data.friendly_name"
                                :error="displayFirstError(validation.friendly_name)" :title="props.authConfigId"></auth-config-form-name>
                        </template>
                    </vx-expandable-card>

                    <template v-if="!props.authConfigId || props.credentials">
                        <template v-if="data.auth_config_type === 'oauth2_client_credentials'">
                            <div v-if="props.authConfigId" class="text-2xl font-extrabold w-full">OAuth Client Credentials</div>
                            <vx-expandable-card id="oauth">
                                <template v-slot:content>
                                    <auth-config-form-client-credentials :title="!props.authConfigId"></auth-config-form-client-credentials>
                                </template>
                            </vx-expandable-card>
                        </template>
                        <template v-else>
                            <div v-if="props.authConfigId" class="text-2xl font-extrabold w-full">Custom Headers</div>
                            <vx-expandable-card id="headers">
                                <template v-slot:content>
                                    <auth-config-form-headers :title="!props.authConfigId"></auth-config-form-headers>
                                </template>
                            </vx-expandable-card>
                        </template>
                    </template>

                    <vx-sticky-footer>
                        <vx-button href="/settings/integrations#/auth-configs"
                            class="w-20"
                            size="lg"
                            color="muted">
                            Cancel
                        </vx-button>
                        <vx-button form="auth-config-form"
                            type="submit"
                            class="grow"
                            size="lg"
                            color="primary"
                            :loading="processing">
                            {{ props.authConfigId ? 'Update' : 'Create' }} API Auth
                        </vx-button>
                    </vx-sticky-footer>
                </form>
            </vx-sticky-nav>
        </div>
    </page-container>
</template>

<script setup>
import {
    VxStickyNav,
    VxStickyFooter,
    VxButton,
    VxExpandableCard,
} from '@voxie/frontend-components';
import StepFormHeading from '../../../../general/StepFormHeading.vue';
import { faArrowLeft, faFingerprint, faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ExperimentalPill from '../../../../general/ExperimentalPill.vue';
import AuthConfigFormName from './AuthConfigFormName.vue';
import AuthConfigFormHeaders from './AuthConfigFormHeaders.vue';
import AuthConfigFormClientCredentials from './AuthConfigFormClientCredentials.vue';
import PageHeader from '../../../../general/PageHeader.vue';
import PageContainer from '../../../../general/PageContainer.vue';
import PageSidebar from '../../../../general/PageSidebar.vue';
import { useAuthConfigForm } from './useAuthConfigForm';
import { onMounted } from 'vue';
import { displayFirstError } from '~/utils/validation';
import { useQueryParams } from '~/composables/useQueryString';
import PageLoading from '~/components/general/PageLoading.vue';
import { useFormSubmission } from '~/composables/useFormSubmission.js';

const queryParams = useQueryParams();

const props = defineProps({
    authConfigId: {
        type: String,
        required: false,
        default: undefined,
    },
    credentials: {
        type: Boolean,
        default: false,
    }
});

const { data, submit: save, loading, defaultData } = useAuthConfigForm();

onMounted(() => {
    data.value.auth_config_id = props.authConfigId;
    data.value.auth_config_type = !data.value.auth_config_id && queryParams.auth_config_type === 'oauth2_client_credentials' ? 'oauth2_client_credentials' : 'custom_header';
});

const { submit, processing, validation, } = useFormSubmission({
    data,
    preserveScroll: () => Boolean(props.authConfigId),
    submit: save,
    reset: () => {
        if (!props.authConfigId) {
            data.value = defaultData();
        }
    },
    notification: () => ({
        message: `API Auth ${props.authConfigId ? 'updated' : 'created'}`,
        action: {
            icon: faSquareArrowUpLeft,
            label: 'Back to API Auths',
            href: '/settings/integrations#/auth-configs',
        },
    })
});
</script>
